import { useMemo } from "react";

import { useAuth } from "@/lib/auth";
import { OrganizationStatus } from "@/enum/common.enum";
import { formatUtcDate } from "@/utils/common.utils";

/**
 * One reading of "can this org still write?", shared by everything that needs it.
 *
 * The backend's `require_billing_ok` returns 402 on every mutating route when the
 * org is `past_due` or `canceled`. Four separate surfaces need to agree with that
 * rule — the app-wide banner, the disabled CTAs, the billing page's headline, and
 * the 402 toast — and if any of them derives it independently they will drift.
 *
 * Reads from `context.billing`, which `AuthProvider` re-polls every 90s, so an
 * expiry that happens mid-session surfaces without a reload.
 */
export interface BillingGate {
  /** Writes are refused by the server: deals, tasks, invitations. */
  blocked: boolean;
  /** New signup with no card on file yet — the app routes to billing until Checkout completes. */
  needsCard: boolean;
  /** Trial is still running but ends within `WARN_WITHIN_DAYS`. */
  endingSoon: boolean;
  status: OrganizationStatus | null;
  /** Days remaining; 0 means "ends today". Null when there is no end date. */
  daysLeft: number | null;
  /** The date the trial ended or the subscription lapsed, already formatted. */
  endedOn: string | null;
  /** Formatted date the trial *will* end, for the pre-expiry warning. */
  endsOn: string | null;
  orgSlug: string | null;
  billingHref: string | null;
  /**
   * One sentence naming exactly what is paused. Empty when not blocked.
   *
   * Kept narrow on purpose: `require_billing_ok` guards only three routes —
   * `create_deal`, `invite_to_org` and `invite_to_deal`. Tasks, documents,
   * deal edits and messages all still write fine, and claiming otherwise would
   * send people to the billing page for something that isn't broken.
   */
  blockedReason: string;
}

export const WARN_WITHIN_DAYS = 3;

export function useBillingGate(): BillingGate {
  const { context } = useAuth();

  return useMemo(() => {
    const billing = context?.billing ?? null;
    const slug = context?.organization?.slug ?? null;
    const isInternal = Boolean(context?.membership);
    const status = (billing?.status as OrganizationStatus) ?? null;
    const daysLeft = typeof billing?.days_left === "number" ? billing.days_left : null;

    // Externals (clients, lenders, attorneys) have no membership and never see
    // billing state — it isn't their subscription and they can't act on it.
    const canceled = status === OrganizationStatus.CANCELED;
    const blocked = isInternal && (status === OrganizationStatus.PAST_DUE || canceled);
    const endingSoon = Boolean(
      isInternal &&
      status === OrganizationStatus.TRIALING &&
      daysLeft !== null &&
      daysLeft <= WARN_WITHIN_DAYS,
    );

    // `trial_end` for a lapsed trial, `active_until` for a lapsed subscription —
    // whichever actually ran out is the date worth naming.
    const endStamp = billing?.active_until || billing?.trial_end || null;

    return {
      blocked,
      needsCard: isInternal && Boolean(billing?.needs_card),
      endingSoon,
      status,
      daysLeft,
      endedOn: blocked && endStamp ? formatUtcDate(endStamp) : null,
      endsOn: endingSoon && billing?.trial_end ? formatUtcDate(billing.trial_end) : null,
      orgSlug: slug,
      billingHref: slug ? `/o/${slug}/billing` : null,
      blockedReason: blocked
        ? canceled
          ? "Your subscription was canceled, so starting new deals and sending invitations is paused."
          : "Your trial has ended, so starting new deals and sending invitations is paused."
        : "",
    };
  }, [context]);
}
